import { Outlet, NavLink, useLocation, Link } from 'react-router-dom';
import { useSpark } from '@estream/sdk-browser/react';
import { useState, useEffect } from 'react';
import TransportStatus from './TransportStatus';
import AlertNotification from './AlertNotification';
import RunbookPanel from './RunbookPanel';
import { isFirstVisit, markFirstVisitComplete, getOverallProgress } from '../services/RunbookService';
import './Layout.css';

// SVG Icon components for professional look
const Icons = {
  Dashboard: () => (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5">
      <rect x="2" y="2" width="7" height="7" rx="1" />
      <rect x="11" y="2" width="7" height="7" rx="1" />
      <rect x="2" y="11" width="7" height="7" rx="1" />
      <rect x="11" y="11" width="7" height="7" rx="1" />
    </svg>
  ),
  Topology: () => (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5">
      <circle cx="10" cy="4" r="2" />
      <circle cx="4" cy="16" r="2" />
      <circle cx="16" cy="16" r="2" />
      <path d="M10 6v4M10 10l-4.5 4M10 10l4.5 4" />
    </svg>
  ),
  Lattices: () => (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path d="M10 2l7 4v8l-7 4-7-4V6l7-4z" />
      <path d="M10 10V18M3 6l7 4 7-4" />
    </svg>
  ),
  Circuits: () => (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path d="M2 10h4M14 10h4M10 2v4M10 14v4" />
      <circle cx="10" cy="10" r="4" />
      <circle cx="10" cy="10" r="1.5" fill="currentColor" />
    </svg>
  ),
  Identity: () => (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5">
      <circle cx="10" cy="6" r="4" />
      <path d="M2 18c0-4 3.5-6 8-6s8 2 8 6" />
    </svg>
  ),
  Approvals: () => (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path d="M17 6l-8 8-4-4" />
      <rect x="2" y="2" width="16" height="16" rx="2" />
    </svg>
  ),
  Governance: () => (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path d="M10 2l8 4v4c0 5-3.5 8-8 10-4.5-2-8-5-8-10V6l8-4z" />
      <path d="M7 10l2 2 4-4" />
    </svg>
  ),
  Bridge: () => (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path d="M2 14h16M4 14V8a6 6 0 0112 0v6" />
      <circle cx="5" cy="8" r="1" fill="currentColor" />
      <circle cx="15" cy="8" r="1" fill="currentColor" />
    </svg>
  ),
  LoadSim: () => (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path d="M2 16l4-6 3 4 4-8 5 10" />
      <rect x="2" y="2" width="16" height="16" rx="2" />
    </svg>
  ),
  Messages: () => (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path d="M2 4h16v10H6l-4 4V4z" rx="1" />
      <path d="M6 8h8M6 11h5" />
    </svg>
  ),
  Infrastructure: () => (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5">
      <rect x="2" y="3" width="16" height="5" rx="1" />
      <rect x="2" y="12" width="16" height="5" rx="1" />
      <circle cx="5" cy="5.5" r="1" fill="currentColor" />
      <circle cx="5" cy="14.5" r="1" fill="currentColor" />
    </svg>
  ),
  Releases: () => (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path d="M10 2v12M6 10l4 4 4-4" />
      <path d="M3 14v2a2 2 0 002 2h10a2 2 0 002-2v-2" />
    </svg>
  ),
  Settings: () => (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5">
      <circle cx="10" cy="10" r="3" />
      <path d="M10 2v2M10 16v2M18 10h-2M4 10H2M15.66 4.34l-1.42 1.42M5.76 14.24l-1.42 1.42M15.66 15.66l-1.42-1.42M5.76 5.76L4.34 4.34" />
    </svg>
  ),
  ChevronDown: () => (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M4 6l4 4 4-4" />
    </svg>
  ),
  Logout: () => (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path d="M10 2H13a1 1 0 011 1v10a1 1 0 01-1 1h-3M6 12l-4-4 4-4M2 8h9" />
    </svg>
  ),
  Tenant: () => (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5">
      <rect x="3" y="2" width="14" height="16" rx="2" />
      <path d="M7 6h.01M10 6h.01M13 6h.01M7 10h.01M10 10h.01M13 10h.01M7 14h6" />
    </svg>
  ),
  Observability: () => (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path d="M2 15l4-4 3 3 5-6 4 4" />
      <circle cx="10" cy="10" r="7" />
      <path d="M10 6v4l2 2" />
    </svg>
  ),
  Debug: () => (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5">
      <circle cx="10" cy="10" r="7" />
      <path d="M10 6v4M10 14h.01" />
      <path d="M6 4L4 2M14 4l2-2M6 16l-2 2M14 16l2 2" />
    </svg>
  ),
  Docs: () => (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path d="M4 2h8l4 4v12a2 2 0 01-2 2H4a2 2 0 01-2-2V4a2 2 0 012-2z" />
      <path d="M12 2v4h4M6 10h8M6 14h5" />
    </svg>
  ),
  Billing: () => (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5">
      <rect x="2" y="4" width="16" height="12" rx="2" />
      <path d="M2 8h16M6 12h2M12 12h2" />
    </svg>
  ),
  Runbooks: () => (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path d="M3 4h14a1 1 0 011 1v10a1 1 0 01-1 1H3a1 1 0 01-1-1V5a1 1 0 011-1z" />
      <path d="M6 8h8M6 11h6M6 14h4" />
      <circle cx="3" cy="4" r="1" fill="currentColor" />
    </svg>
  ),
  Fees: () => (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5">
      <line x1="10" y1="2" x2="10" y2="18" />
      <path d="M14 6H8a2.5 2.5 0 000 5h4a2.5 2.5 0 010 5H6" />
    </svg>
  ),
  Team: () => (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5">
      <circle cx="7" cy="5" r="3" />
      <circle cx="13" cy="9" r="3" />
      <path d="M1 17c0-3 2.7-5 6-5s6 2 6 5" />
      <path d="M8 17c0-2 2-4 5-4s5 2 5 4" />
    </svg>
  ),
  Logo: () => (
    <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
      <defs>
        <linearGradient id="logo-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#3b82f6" />
          <stop offset="100%" stopColor="#8b5cf6" />
        </linearGradient>
      </defs>
      <path 
        d="M16 2L28 9v14l-12 7L4 23V9l12-7z" 
        fill="url(#logo-gradient)" 
        opacity="0.2"
      />
      <path 
        d="M16 2L28 9v14l-12 7L4 23V9l12-7z" 
        stroke="url(#logo-gradient)" 
        strokeWidth="2"
        fill="none"
      />
      <path 
        d="M16 10v12M10 13l6 3.5 6-3.5" 
        stroke="url(#logo-gradient)" 
        strokeWidth="2"
      />
    </svg>
  ),
};

interface NavItem {
  path: string;
  icon: keyof typeof Icons;
  label: string;
  badgeKey?: string; // Key for dynamic badge count
  subItems?: { path: string; label: string }[];
}

interface NavSection {
  title?: string;
  items: NavItem[];
}

// Navigation sections following the genesis-first architecture
// See: #216 Console Redesign, #259 Navigation Restructure
const NAV_SECTIONS: NavSection[] = [
  {
    items: [
      { path: '/', icon: 'Dashboard', label: 'Dashboard' },
    ],
  },
  {
    title: 'Platform',
    items: [
      { path: '/topology', icon: 'Topology', label: 'Network Topology' },
      { 
        path: '/lattices', 
        icon: 'Lattices', 
        label: 'Lattices',
        subItems: [
          { path: '/lattices', label: 'Overview' },
          { path: '/lattices/nodes', label: 'Nodes' },
        ],
      },
      { path: '/circuits', icon: 'Circuits', label: 'Circuits' },
      { path: '/infrastructure', icon: 'Infrastructure', label: 'Infrastructure' },
      { path: '/releases', icon: 'Releases', label: 'Releases' },
    ],
  },
  {
    // Governance is the central hub for all approvals and policy management
    title: 'Governance',
    items: [
      { path: '/governance', icon: 'Governance', label: 'Active Proposals', badgeKey: 'pendingApprovals' },
      { path: '/governance/activity', icon: 'Messages', label: 'Recent Activity' },
      { path: '/governance/key-holders', icon: 'Identity', label: 'Key Holders' },
      { path: '/governance/policies', icon: 'Settings', label: 'Policies' },
    ],
  },
  {
    title: 'Operations',
    items: [
      { path: '/team', icon: 'Team', label: 'Agent Team' },
      { path: '/messages', icon: 'Messages', label: 'Messages' },
      { path: '/loadsim', icon: 'LoadSim', label: 'Load Simulator' },
      { path: '/observability', icon: 'Observability', label: 'Observability' },
      { path: '/bridge', icon: 'Bridge', label: 'Bridge' },
    ],
  },
];

// Tenant section - only shown for tenant role users
// This is separate because it's conditionally rendered
const TENANT_SECTION: NavSection = {
  title: 'Tenant',
  items: [
    { path: '/tenant', icon: 'Tenant', label: 'My Lattices' },
    { path: '/tenant/subscription', icon: 'Docs', label: 'Subscription' },
    { path: '/billing', icon: 'Billing', label: 'Billing' },
  ],
};

// Breadcrumb configuration
const BREADCRUMB_MAP: Record<string, { label: string; parent?: string }> = {
  '/': { label: 'Dashboard' },
  // Platform
  '/topology': { label: 'Network Topology' },
  '/lattices': { label: 'Lattices' },
  '/lattices/nodes': { label: 'Nodes', parent: '/lattices' },
  '/lattices/circuits': { label: 'Circuits', parent: '/lattices' },
  '/circuits': { label: 'Circuits' },
  '/circuits/debug': { label: 'Debugger', parent: '/circuits' },
  '/infrastructure': { label: 'Infrastructure' },
  '/releases': { label: 'Releases' },
  // Governance
  '/governance': { label: 'Active Proposals' },
  '/governance/activity': { label: 'Recent Activity', parent: '/governance' },
  '/governance/key-holders': { label: 'Key Holders', parent: '/governance' },
  '/governance/policies': { label: 'Policies', parent: '/governance' },
  '/governance/approvals': { label: 'Approvals', parent: '/governance' }, // Legacy redirect
  // Operations
  '/team': { label: 'Agent Team' },
  '/messages': { label: 'Messages' },
  '/loadsim': { label: 'Load Simulator' },
  '/observability': { label: 'Observability' },
  '/bridge': { label: 'Bridge' },
  // Tenant
  '/tenant': { label: 'My Lattices' },
  '/tenant/subscription': { label: 'Subscription', parent: '/tenant' },
  '/billing': { label: 'Billing' },
  // Other
  '/settings': { label: 'Settings' },
  '/genesis': { label: 'Genesis Wizard' },
  '/docs': { label: 'API Docs' },
  '/fees': { label: 'Fees & Pricing' },
};

export default function Layout() {
  const location = useLocation();
  const { session, signOut } = useSpark();
  const [collapsedSections, setCollapsedSections] = useState<Set<string>>(new Set());
  const [badges, setBadges] = useState<Record<string, number>>({});
  const [runbookPanelOpen, setRunbookPanelOpen] = useState(false);
  const [showGettingStarted, setShowGettingStarted] = useState(isFirstVisit());
  const [runbookProgress] = useState(getOverallProgress);
  
  // Determine if user has tenant role (show tenant section)
  // For now, show tenant section for all authenticated users
  // In production, this would check session.role or session.permissions
  const showTenantSection = session !== null;
  
  // Fetch badge counts
  useEffect(() => {
    const fetchBadges = async () => {
      try {
        const edgeUrl = import.meta.env.VITE_EDGE_URL || 'https://edge.estream.dev';
        const response = await fetch(`${edgeUrl}/api/governance/pending-count`);
        if (response.ok) {
          const data = await response.json();
          setBadges({ pendingApprovals: data.count || 0 });
        }
      } catch {
        // Ignore errors, badges are optional
      }
    };
    
    fetchBadges();
    const interval = setInterval(fetchBadges, 30000); // Refresh every 30s
    return () => clearInterval(interval);
  }, []);
  
  const toggleSection = (title: string) => {
    setCollapsedSections(prev => {
      const next = new Set(prev);
      if (next.has(title)) {
        next.delete(title);
      } else {
        next.add(title);
      }
      return next;
    });
  };
  
  const getNetworkBadge = () => {
    const hostname = window.location.hostname;
    const match = hostname.match(/console\.(.+)\.estream\./);
    return match ? match[1] : 'alpha-devnet';
  };
  
  const getCurrentPageTitle = () => {
    const config = BREADCRUMB_MAP[location.pathname];
    return config?.label || 'Dashboard';
  };
  
  const getBreadcrumbs = () => {
    const crumbs: { path: string; label: string }[] = [];
    let currentPath = location.pathname;
    
    // Build breadcrumb chain
    while (currentPath) {
      const config = BREADCRUMB_MAP[currentPath];
      if (config) {
        crumbs.unshift({ path: currentPath, label: config.label });
        currentPath = config.parent || '';
      } else {
        break;
      }
    }
    
    return crumbs;
  };
  
  const isPathActive = (path: string) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };
  
  return (
    <div className="layout">
      {/* Sidebar */}
      <aside className="sidebar">
        <div className="sidebar-header">
          <div className="logo">
            <Icons.Logo />
            <div className="logo-text">
              <span className="logo-name">eStream</span>
              <span className="logo-badge">Console</span>
            </div>
          </div>
        </div>
        
        <nav className="sidebar-nav">
          {/* Main navigation sections */}
          {NAV_SECTIONS.map((section, sectionIndex) => (
            <div key={sectionIndex} className="nav-section">
              {section.title && (
                <button 
                  className={`nav-section-header ${section.title === 'Governance' ? 'governance-header' : ''}`}
                  onClick={() => toggleSection(section.title!)}
                >
                  <span className="nav-section-title">{section.title}</span>
                  {/* Show badge on Governance section header */}
                  {section.title === 'Governance' && badges.pendingApprovals > 0 && (
                    <span className="nav-section-badge">{badges.pendingApprovals > 99 ? '99+' : badges.pendingApprovals}</span>
                  )}
                  <span className={`nav-section-chevron ${collapsedSections.has(section.title) ? 'collapsed' : ''}`}>
                    <Icons.ChevronDown />
                  </span>
                </button>
              )}
              
              {!section.title || !collapsedSections.has(section.title) ? (
                <div className="nav-items">
                  {section.items.map((item) => {
                    const IconComponent = Icons[item.icon];
                    const badgeCount = item.badgeKey ? badges[item.badgeKey] : 0;
                    return (
                      <NavLink
                        key={item.path}
                        to={item.path}
                        className={() => 
                          `nav-item ${isPathActive(item.path) ? 'active' : ''}`
                        }
                        end={item.path === '/' || !item.subItems}
                      >
                        <span className="nav-icon">
                          <IconComponent />
                        </span>
                        <span className="nav-label">{item.label}</span>
                        {badgeCount > 0 && (
                          <span className="nav-badge">{badgeCount > 99 ? '99+' : badgeCount}</span>
                        )}
                      </NavLink>
                    );
                  })}
                </div>
              ) : null}
            </div>
          ))}
          
          {/* Tenant section - conditionally shown based on user role */}
          {showTenantSection && (
            <div className="nav-section">
              <button 
                className="nav-section-header"
                onClick={() => toggleSection(TENANT_SECTION.title!)}
              >
                <span className="nav-section-title">{TENANT_SECTION.title}</span>
                <span className={`nav-section-chevron ${collapsedSections.has(TENANT_SECTION.title!) ? 'collapsed' : ''}`}>
                  <Icons.ChevronDown />
                </span>
              </button>
              
              {!collapsedSections.has(TENANT_SECTION.title!) && (
                <div className="nav-items">
                  {TENANT_SECTION.items.map((item) => {
                    const IconComponent = Icons[item.icon];
                    return (
                      <NavLink
                        key={item.path}
                        to={item.path}
                        className={() => 
                          `nav-item ${isPathActive(item.path) ? 'active' : ''}`
                        }
                        end
                      >
                        <span className="nav-icon">
                          <IconComponent />
                        </span>
                        <span className="nav-label">{item.label}</span>
                      </NavLink>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </nav>
        
        <div className="sidebar-footer">
          <div className="connection-status">
            <TransportStatus />
          </div>
          
          <button 
            className="nav-item runbooks-link"
            onClick={() => setRunbookPanelOpen(true)}
          >
            <span className="nav-icon"><Icons.Runbooks /></span>
            <span className="nav-label">Runbooks</span>
            {runbookProgress.percentage < 100 && (
              <span className="nav-progress">{runbookProgress.percentage}%</span>
            )}
          </button>
          
          <NavLink to="/settings" className="nav-item settings-link">
            <span className="nav-icon"><Icons.Settings /></span>
            <span className="nav-label">Settings</span>
          </NavLink>
          
          {session && (
            <div className="user-section">
              <div className="user-info">
                <div className="user-avatar">
                  {(session.displayName || 'O').charAt(0).toUpperCase()}
                </div>
                <div className="user-details">
                  <span className="user-name">{session.displayName || 'Operator'}</span>
                  <span className="user-role">Administrator</span>
                </div>
              </div>
              <button className="btn-logout" onClick={signOut} title="Sign out">
                <Icons.Logout />
              </button>
            </div>
          )}
        </div>
      </aside>
      
      {/* Main Content */}
      <main className="main-content">
        <header className="content-header">
          <div className="header-left">
            {getBreadcrumbs().length > 1 ? (
              <nav className="breadcrumbs">
                {getBreadcrumbs().map((crumb, index, arr) => (
                  <span key={crumb.path} className="breadcrumb-item">
                    {index < arr.length - 1 ? (
                      <>
                        <Link to={crumb.path} className="breadcrumb-link">{crumb.label}</Link>
                        <span className="breadcrumb-sep">/</span>
                      </>
                    ) : (
                      <span className="breadcrumb-current">{crumb.label}</span>
                    )}
                  </span>
                ))}
              </nav>
            ) : (
              <h1 className="page-title">{getCurrentPageTitle()}</h1>
            )}
          </div>
          
          <div className="header-right">
            <AlertNotification />
            <div className="network-badge">
              <span className="network-dot"></span>
              <span className="network-name">{getNetworkBadge()}</span>
            </div>
          </div>
        </header>
        
        <div className="content-body">
          {/* Getting Started Banner for first-time visitors */}
          {showGettingStarted && location.pathname === '/' && (
            <div className="getting-started-banner">
              <div className="banner-icon">🚀</div>
              <div className="banner-content">
                <h3 className="banner-title">Welcome to eStream Console</h3>
                <p className="banner-description">
                  New here? Follow our Getting Started guide to deploy your first lattice and circuit.
                </p>
              </div>
              <button 
                className="banner-action"
                onClick={() => setRunbookPanelOpen(true)}
              >
                Get Started
              </button>
              <button 
                className="dismiss-btn"
                onClick={() => {
                  markFirstVisitComplete();
                  setShowGettingStarted(false);
                }}
                title="Dismiss"
              >
                ×
              </button>
            </div>
          )}
          <Outlet />
        </div>
      </main>
      
      {/* Runbook Panel */}
      <RunbookPanel 
        isOpen={runbookPanelOpen}
        onClose={() => setRunbookPanelOpen(false)}
        initialRunbookId={showGettingStarted ? 'getting-started' : undefined}
      />
    </div>
  );
}
